import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Loader } from 'lucide-react';
import { Button } from '../ui/Button';
import { subscriptionApi } from '../../services/subscriptionApi';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { Subscription } from '../../types';

interface SubscriptionSuccessProps {
  sessionId: string;
  onComplete: () => void;
}

export const SubscriptionSuccess: React.FC<SubscriptionSuccessProps> = ({ sessionId, onComplete }) => {
  const { loadSubscription, loadFeatureAccess } = useSubscriptionStore();
  
  const [isProcessing, setIsProcessing] = useState(true);
  const [message, setMessage] = useState('');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCheckoutSuccess = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setIsProcessing(false);
        return;
      }

      try {
        const result = await subscriptionApi.handleCheckoutSuccess(sessionId);
        
        if (result.success) {
          setMessage(result.message);
          setSubscription(result.subscription || null);
          
          // Reload subscription and feature access data
          await Promise.all([
            loadSubscription(),
            loadFeatureAccess()
          ]);
        } else {
          setError(result.message);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process checkout';
        setError(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    };

    handleCheckoutSuccess();
  }, [sessionId, loadSubscription, loadFeatureAccess]);

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <Loader className="w-12 h-12 text-blue-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Processing Your Subscription
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we confirm your payment and set up your subscription...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-2xl">✕</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Payment Processing Error
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
            <Button onClick={onComplete} variant="outline">
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Welcome to Koru!
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {message || 'Your subscription has been activated successfully.'}
          </p>

          {subscription && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Subscription Details
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <div>Plan: {subscription.tier}</div>
                <div>Status: {subscription.status}</div>
                {subscription.isInTrial && subscription.trialEnd && (
                  <div>
                    Trial ends: {new Date(subscription.trialEnd).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Button 
              onClick={onComplete}
              className="w-full"
              rightIcon={<ArrowRight size={16} />}
            >
              Continue to Dashboard
            </Button>
            
            <p className="text-xs text-gray-500 dark:text-gray-400">
              You can manage your subscription anytime from your account settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 